import {
  Alert,
  Box,
  Button,
  Group,
  Loader,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import {
  IconArrowRight,
  IconGitBranch,
  IconHistory,
} from "@tabler/icons-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import dayjs from "dayjs";
import { casesService } from "../../services/cases.service";
import { casePathologistAssignmentQueryKeys } from "../../queryKeys/casePathologistAssignmentQueryKeys";

interface PathologistAssignmentHistoryProps {
  caseId: string;
}

const HISTORY_PAGE_SIZE = 50;

export default function PathologistAssignmentHistory({
  caseId,
}: PathologistAssignmentHistoryProps) {
  const historyQuery = useInfiniteQuery({
    queryKey: casePathologistAssignmentQueryKeys.history(caseId),
    queryFn: ({ pageParam }) =>
      casesService.getCasePathologistAssignmentHistory(
        caseId,
        pageParam,
        HISTORY_PAGE_SIZE
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.page < lastPage.pagination.totalPages
        ? lastPage.pagination.page + 1
        : undefined,
    enabled: Boolean(caseId),
  });
  const events = useMemo(
    () =>
      [...(historyQuery.data?.pages.flatMap(({ data }) => data) ?? [])].sort(
        (first, second) =>
          new Date(second.occurredAt).getTime() -
          new Date(first.occurredAt).getTime()
      ),
    [historyQuery.data?.pages]
  );
  const { fetchNextPage } = historyQuery;
  const handleLoadMore = useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);

  if (historyQuery.isLoading) {
    return (
      <Group justify="center" py="lg">
        <Loader size="sm" aria-label="Loading assignment history" />
      </Group>
    );
  }

  if (historyQuery.isError) {
    return (
      <Alert color="red" title="Assignment history unavailable" role="alert">
        Refresh the case to try again.
      </Alert>
    );
  }

  if (events.length === 0) {
    return (
      <Text size="sm" c="dimmed" py="md">
        No pathologist assignment changes recorded.
      </Text>
    );
  }

  return (
    <Stack gap="md">
      <Stack
        gap="md"
        role="list"
        aria-label="Pathologist responsibility history"
      >
        {events.map((event) => (
          <Box
            key={event.id}
            role="listitem"
            aria-label={`${event.fromPathologist.fullName} reassigned to ${event.toPathologist.fullName}`}
          >
            <Group align="flex-start" wrap="nowrap">
              <ThemeIcon variant="light" color="gray" aria-hidden="true">
                {event.transitionSource === "parent_inheritance" ? (
                  <IconGitBranch size={17} />
                ) : (
                  <IconHistory size={17} />
                )}
              </ThemeIcon>
              <Stack gap={3} miw={0} flex={1}>
                <Text size="xs" fw={700} tt="uppercase">
                  {event.transitionSource === "parent_inheritance"
                    ? "Inherited assignment"
                    : "Reassigned"}
                </Text>
                <Group gap="xs">
                  <Text size="sm" fw={700}>
                    {event.fromPathologist.fullName}
                  </Text>
                  <IconArrowRight size={15} aria-hidden="true" />
                  <Text size="sm" fw={700}>
                    {event.toPathologist.fullName}
                  </Text>
                </Group>
                <Text size="xs">
                  Changed by {event.changedBy.fullName} ·{" "}
                  {event.lifecycleStatus}
                </Text>
                <Text size="sm">{event.reason}</Text>
                <Text size="xs" c="dimmed">
                  {dayjs(event.occurredAt).format("MMM D, YYYY h:mm A")}
                </Text>
                {event.transitionSource === "parent_inheritance" && (
                  <Text size="xs" fw={600}>
                    Inherited from parent case assignment
                  </Text>
                )}
              </Stack>
            </Group>
          </Box>
        ))}
      </Stack>
      {historyQuery.hasNextPage && (
        <Button
          variant="light"
          onClick={handleLoadMore}
          loading={historyQuery.isFetchingNextPage}
          aria-label="Load more assignment history"
        >
          Load more
        </Button>
      )}
    </Stack>
  );
}
